import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Delaunay } from "d3-delaunay";
import "./PageTransition.css";

type Shard = { clip: string; tx: number; ty: number; rot: number; delay: number };

function buildShards(count: number): Shard[] {
  const seeds: [number, number][] = [];
  for (let i = 0; i < count; i++) {
    let x: number, y: number;
    if (i < count * 0.45) {
      const ang = Math.random() * Math.PI * 2;
      const rad = Math.pow(Math.random(), 0.7) * 40;
      x = 50 + Math.cos(ang) * rad * 1.5;
      y = 50 + Math.sin(ang) * rad;
    } else {
      x = Math.random() * 100;
      y = Math.random() * 100;
    }
    seeds.push([Math.max(0, Math.min(100, x)), Math.max(0, Math.min(100, y))]);
  }
  const voronoi = Delaunay.from(seeds).voronoi([0, 0, 100, 100]);
  const shards: Shard[] = [];
  for (let i = 0; i < seeds.length; i++) {
    const cell = voronoi.cellPolygon(i);
    if (!cell) continue;
    const clip =
      "polygon(" + cell.map((p: number[]) => `${p[0].toFixed(2)}% ${p[1].toFixed(2)}%`).join(", ") + ")";
    let cx = 0, cy = 0;
    cell.forEach((p: number[]) => { cx += p[0]; cy += p[1]; });
    cx /= cell.length; cy /= cell.length;
    const dx = cx - 50, dy = cy - 50;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    const centerBoost = (1 - Math.min(dist / 55, 1)) * 30;
    const reach = 45 + centerBoost + Math.random() * 60;
    const tx = (dx / dist) * reach + (Math.random() - 0.5) * 22;
    const ty = (dy / dist) * reach + (Math.random() - 0.5) * 22 + 6;
    const rot = (Math.random() - 0.5) * 140;
    const delay = Math.random() * 0.3;
    shards.push({ clip, tx, ty, rot, delay });
  }
  return shards;
}

/**
 * Glass-shatter page transition, correctly sequenced:
 *   1. user clicks an internal link -> we intercept
 *   2. a whole frosted-glass pane COVERS the screen (current page still behind)
 *   3. once covered, we navigate (new page mounts hidden under the glass)
 *   4. the glass SHATTERS away, revealing the new page
 * No page capture (so it's light + crash-proof). Section-scroll links keep
 * working because we replay the original navigation intent.
 */
export default function PageTransition({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [shards, setShards] = useState<Shard[]>([]);
  const [phase, setPhase] = useState<"idle" | "cover" | "shatter">("idle");
  const timers = useRef<number[]>([]);
  const animating = useRef(false);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const a = (e.target as HTMLElement)?.closest?.("a") as HTMLAnchorElement | null;
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (
        !href.startsWith("/") || href.startsWith("//") ||
        a.target === "_blank" || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0
      ) return;

      const hashIdx = href.indexOf("#");
      const targetPath = hashIdx >= 0 ? href.slice(0, hashIdx).replace(/\/$/, "") || "/" : href;
      const targetHash = hashIdx >= 0 ? href.slice(hashIdx + 1) : "";
      const currentPath = location.pathname.replace(/\/$/, "") || "/";
      if (targetPath === currentPath) return; // same page -> let normal scroll happen

      if (animating.current) { e.preventDefault(); return; }
      e.preventDefault();
      e.stopPropagation();
      animating.current = true;

      const fine = typeof window !== "undefined" && window.innerWidth >= 820;
      setShards(buildShards(fine ? 150 : 90));

      // 1) drop the whole glass cover over the CURRENT page
      setPhase("cover");

      timers.current.forEach((t) => window.clearTimeout(t));
      timers.current = [];

      // 2) once the cover is solidly down, navigate underneath it
      timers.current.push(
        window.setTimeout(() => {
          if (targetHash) {
            navigate(targetPath, { state: { faScrollTo: targetHash } });
          } else {
            navigate(href);
          }
        }, 180)
      );

      // 3) give the new page a beat to mount hidden under the glass, then shatter
      timers.current.push(window.setTimeout(() => setPhase("shatter"), 520));

      // 4) clean up
      timers.current.push(
        window.setTimeout(() => {
          setPhase("idle");
          animating.current = false;
        }, 2500)
      );
    };

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [location.pathname, navigate]);

  useEffect(() => {
    return () => timers.current.forEach((t) => window.clearTimeout(t));
  }, []);

  return (
    <>
      <div className="pt-page">{children}</div>

      {phase !== "idle" && (
        <div
          className={`pt-overlay pt-overlay--glass ${phase === "shatter" ? "is-shatter" : "is-cover"}`}
          aria-hidden="true"
        >
          {shards.map((s, i) => (
            <div
              key={i}
              className="pt-shard"
              style={
                {
                  clipPath: s.clip,
                  WebkitClipPath: s.clip,
                  "--tx": `${s.tx}vmax`,
                  "--ty": `${s.ty}vmax`,
                  "--rot": `${s.rot}deg`,
                  "--delay": `${s.delay}s`,
                } as React.CSSProperties
              }
            />
          ))}
        </div>
      )}
    </>
  );
}
